import React, { useState, useEffect } from 'react';
import { UserCheck, Info, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ReviewerDropdown } from '../common/ReviewerDropdown';

// Define the structure for a reviewer
interface Reviewer {
  id: string;
  firstName: string;
  lastName: string;
  photoURL?: string;
  // Add other relevant fields if needed, e.g., email, profile picture
}

interface ApprovalCardProps {
  requiresApproval: boolean;
  selectedReviewerId: string | null;
  reviewers: Reviewer[]; // List of potential reviewers (users with canPublish: true)
  onApprovalToggle: (requires: boolean) => void;
  onReviewerChange: (reviewerId: string | null) => void;
  isLoadingReviewers?: boolean; // Optional loading state for reviewers
  currentUserId?: string | null; // Added prop for current user ID
  actualPostStatus?: string; // New prop for actual post status
  finalReviewer?: { // Changed from finalReviewerName to finalReviewer object
    id: string;
    name: string;
    photoURL?: string;
    firstName?: string;
    lastName?: string;
  } | null;
  finalReviewComments?: string | null; // New prop for comments from approval/rejection
}

export const ApprovalCard: React.FC<ApprovalCardProps> = ({
  requiresApproval,
  selectedReviewerId,
  reviewers = [], // Default to empty array
  onApprovalToggle,
  onReviewerChange,
  isLoadingReviewers = false, // Default to false
  currentUserId = null, // Added prop
  actualPostStatus,             // Destructure new prop
  finalReviewer,          // Changed from finalReviewerName
  finalReviewComments         // Destructure new prop
}) => {
  const { t } = useTranslation();
  const [selectedReviewer, setSelectedReviewer] = useState<Reviewer | null>(null);

  // Determine if a final decision (approved/rejected) has been made and is being displayed
  const hasFinalDecision = 
    ((actualPostStatus === 'ready_to_publish' || actualPostStatus === 'scheduled' || actualPostStatus === 'published') && finalReviewer) || 
    (actualPostStatus === 'rejected' && finalReviewer);

  // Effect to find the selected reviewer object when the ID changes
  useEffect(() => {
    if (selectedReviewerId) {
      const reviewer = reviewers.find(r => r.id === selectedReviewerId) || null;
      setSelectedReviewer(reviewer);
    } else {
      setSelectedReviewer(null);
    }
  }, [selectedReviewerId, reviewers]);

  // Handler for the toggle switch
  const handleToggleApproval = () => {
    const newRequiresApproval = !requiresApproval;
    onApprovalToggle(newRequiresApproval);
    // If turning off approval, clear the selected reviewer
    if (!newRequiresApproval) {
      onReviewerChange(null);
    } else if (reviewers.length > 0 && !selectedReviewerId) {
      // If turning on and no reviewer selected, select the first one by default
      onReviewerChange(reviewers[0].id);
    }
  };

  // Handler for reviewer selection change
  const handleReviewerSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const reviewerId = event.target.value || null;
    onReviewerChange(reviewerId);
  };

  // Determine if the dropdown should be enabled
  const isDropdownDisabled = !requiresApproval || isLoadingReviewers || reviewers.length === 0;

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <UserCheck className="w-5 h-5 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">
          {hasFinalDecision 
            ? t('cards.approval.statusTitle', 'Approval Status') 
            : t('cards.approval.title', 'Approval Required')}
        </h2>
      </div>

      <div className="space-y-6">
        {!hasFinalDecision && (
          <>
            {/* Approval Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">{t('cards.approval.toggleLabel', 'Send for approval')}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {t('cards.approval.toggleDescription', 'This post will require approval from the reviewer you select below.')}
                </p>
              </div>
              <button
                type="button"
                onClick={handleToggleApproval}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  requiresApproval ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    requiresApproval ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Reviewer Selection Dropdown */}
            <div className={requiresApproval ? 'opacity-100' : 'opacity-50 pointer-events-none'}>
              <div>
                <label htmlFor="reviewer-select" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('cards.approval.reviewerLabel', 'Select Reviewer')}
                </label>
                <ReviewerDropdown 
                   reviewers={reviewers}
                   selectedReviewerId={selectedReviewerId}
                   onReviewerSelect={onReviewerChange}
                   isLoading={isLoadingReviewers}
                   disabled={!requiresApproval || isLoadingReviewers}
                   placeholder={t('cards.approval.selectPlaceholder', 'Select a reviewer...')} 
                   currentUserId={currentUserId}
                />
              </div>

              {requiresApproval && !selectedReviewerId && reviewers.length > 0 && (
                <div className="mt-3 flex items-start gap-2 text-orange-600 bg-orange-50 p-3 rounded-lg">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">
                    {t('cards.approval.selectReviewerWarning', 'Please select a reviewer for this post.')}
                  </p>
                </div>
              )}
               {requiresApproval && reviewers.length === 0 && !isLoadingReviewers && (
                <div className="mt-3 flex items-start gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">
                    {t('cards.approval.noReviewersError', 'No eligible reviewers found for the selected brand. Cannot request approval.')}
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Approval Status Section: Shows past decision OR future/current setup */}
        <div className="pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            {t('cards.approval.statusTitle', 'Approval Status')}
          </h4>
          
          {hasFinalDecision ? (
            // Display past approval/rejection details
            (actualPostStatus === 'ready_to_publish' || actualPostStatus === 'scheduled' || actualPostStatus === 'published') && finalReviewer ? (
              <div className="space-y-2">
                <div className="flex items-center text-sm text-green-600">
                  <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{t('cards.approval.approvedBy', 'Approved by')}: <strong>{finalReviewer.name}</strong></span>
                  {finalReviewer.photoURL ? (
                    <img 
                      src={finalReviewer.photoURL} 
                      alt={finalReviewer.name}
                      className="w-5 h-5 rounded-full object-cover ml-2"
                    />
                  ) : (finalReviewer.firstName || finalReviewer.lastName) && (
                    <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 ml-2 flex-shrink-0">
                      {finalReviewer.firstName?.charAt(0).toUpperCase()}{finalReviewer.lastName?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                {finalReviewComments && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-600 ml-6 mb-0.5">{t('cards.approval.commentsLabel', 'Comments')}:</h5>
                    <div className="text-xs text-gray-600 ml-6 italic bg-gray-50 p-2 rounded whitespace-pre-wrap">
                      {finalReviewComments}
                    </div>
                  </div>
                )}
              </div>
            ) : actualPostStatus === 'rejected' && finalReviewer ? (
              <div className="space-y-2">
                <div className="flex items-center text-sm text-red-600">
                  <XCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{t('cards.approval.rejectedBy', 'Rejected by')}: <strong>{finalReviewer.name}</strong></span>
                  {finalReviewer.photoURL ? (
                    <img 
                      src={finalReviewer.photoURL} 
                      alt={finalReviewer.name}
                      className="w-5 h-5 rounded-full object-cover ml-2"
                    />
                  ) : (finalReviewer.firstName || finalReviewer.lastName) && (
                    <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 ml-2 flex-shrink-0">
                      {finalReviewer.firstName?.charAt(0).toUpperCase()}{finalReviewer.lastName?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                {finalReviewComments && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-600 ml-6 mb-0.5">{t('cards.approval.commentsLabel', 'Comments')}:</h5>
                    <div className="text-xs text-gray-600 ml-6 italic bg-gray-50 p-2 rounded whitespace-pre-wrap">
                      {finalReviewComments}
                    </div>
                  </div>
                )}
              </div>
            ) : null // Should not happen if hasFinalDecision is true and derived correctly
          ) : (
            // Display current/future approval setup (if no final decision shown yet)
            <div className="flex items-center text-sm">
              <UserCheck className="w-4 h-4 text-gray-400 flex-shrink-0 mr-2" />
              {requiresApproval && selectedReviewer ? (
                <span className="text-primary-600 flex items-center">
                  {/* Text first */}
                  {t('cards.approval.statusRequired', 'Will be reviewed by {{reviewerName}}', { reviewerName: `${selectedReviewer.firstName} ${selectedReviewer.lastName}` })}
                  {/* Then image/initials */}
                  {selectedReviewer.photoURL ? (
                    <img 
                      src={selectedReviewer.photoURL} 
                      alt={`${selectedReviewer.firstName} ${selectedReviewer.lastName}`}
                      className="w-5 h-5 rounded-full object-cover ml-2"
                    />
                  ) : (
                    <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 ml-2 flex-shrink-0">
                      {selectedReviewer.firstName?.charAt(0)}{selectedReviewer.lastName?.charAt(0)}
                    </span>
                  )}
                </span>
              ) : requiresApproval && !selectedReviewer && reviewers.length > 0 ? (
                <span className="text-orange-600 ml-2">
                  {t('cards.approval.statusPendingSelection', 'Awaiting reviewer selection')}
                </span>
              ): requiresApproval && reviewers.length === 0 ? (
                <span className="text-red-600 ml-2">
                  {t('cards.approval.statusNoReviewersAvailable', 'No reviewers available')}
                </span>
              ) : (
                <span className="text-gray-600 ml-2">
                  {t('cards.approval.statusNotRequired', 'Will not be reviewed')}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 